import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Zap, Scale, Timer } from 'lucide-react';

const InferenceComputeSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="More compute at inference = better answers, but at what cost?"
      >
        Inference-Time Compute Scaling
      </SlideTitle>
      
      <SlideContent>
        {/* Trade-off table */}
        <div className="slide-card overflow-hidden mb-8">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Method</th>
                <th>Samples</th>
                <th>Time</th>
                <th>Accuracy</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-accent" />
                    <span className="text-foreground">Fast</span>
                  </span>
                </td>
                <td className="text-muted-foreground">Greedy</td>
                <td className="font-mono text-muted-foreground">1</td>
                <td className="font-mono text-accent">1s</td>
                <td className="font-mono text-muted-foreground">70%</td>
              </tr>
              <tr>
                <td>
                  <span className="flex items-center gap-2">
                    <Scale className="w-4 h-4 text-chart-4" />
                    <span className="text-foreground">Balanced</span>
                  </span>
                </td>
                <td className="text-muted-foreground">Self-consistency</td>
                <td className="font-mono text-muted-foreground">3</td>
                <td className="font-mono text-chart-4">10s</td>
                <td className="font-mono text-chart-4">90%</td>
              </tr>
              <tr>
                <td>
                  <span className="flex items-center gap-2">
                    <Timer className="w-4 h-4 text-primary" />
                    <span className="text-foreground">Accurate</span>
                  </span>
                </td>
                <td className="text-muted-foreground">Tree-of-thoughts</td>
                <td className="font-mono text-muted-foreground">10</td>
                <td className="font-mono text-primary">50s</td>
                <td className="font-mono text-primary">95%</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* NOC Strategy */}
        <div className="slide-card">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">NOC Strategy</h3>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-accent" />
                <span className="font-heading font-semibold text-foreground">Routine Monitoring</span>
              </div>
              <p className="text-sm text-muted-foreground">Use fast greedy decoding for constant health checks</p>
              <div className="mt-3 text-xs text-accent">Method: Greedy (1 sample)</div>
            </div>
            
            <div className="p-4 rounded-lg bg-chart-4/10 border border-chart-4/30">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-5 h-5 text-chart-4" />
                <span className="font-heading font-semibold text-foreground">Active Incident</span>
              </div>
              <p className="text-sm text-muted-foreground">Balance speed and accuracy during live issues</p>
              <div className="mt-3 text-xs text-chart-4">Method: Self-consistency (3-5 samples)</div>
            </div>
            
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="w-5 h-5 text-primary" />
                <span className="font-heading font-semibold text-foreground">Root Cause Analysis</span>
              </div>
              <p className="text-sm text-muted-foreground">Maximum accuracy for post-incident review</p>
              <div className="mt-3 text-xs text-primary">Method: Tree-of-thoughts (10+ samples)</div>
            </div>
          </div>
        </div>
        
        {/* Key insight */}
        <div className="mt-6 p-4 rounded-xl bg-secondary/10 border border-secondary/20">
          <p className="text-sm text-muted-foreground">
            <strong className="text-secondary">Key Insight:</strong> Match inference compute to task criticality. Not every query needs 50 seconds of reasoning.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default InferenceComputeSlide;
